import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Spinner from "../../../components/Spinner";
import { teacherSchoolTasksApi } from "../api";
import type { SchoolTaskSettings, SchoolTaskSettingsChange } from "../types";
import { formatLocalDate } from "./AutoAssignmentPreviewDialog";

/** The School tasks settings of a school year, loaded only when the user may read them. */
export function useSchoolTaskSettings(schoolYearId: string, canRead: boolean) {
  const [settings, setSettings] = useState<SchoolTaskSettings | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setSettings(null);
    setLoadFailed(false);
    if (!schoolYearId || !canRead) return undefined;
    teacherSchoolTasksApi.settings(schoolYearId)
      .then((loaded) => { if (active) setSettings(loaded); })
      .catch(() => { if (active) setLoadFailed(true); });
    return () => { active = false; };
  }, [schoolYearId, canRead]);

  return { settings, loadFailed, setSettings };
}

export interface SettingsSectionProps {
  settings: SchoolTaskSettings | null;
  loadFailed: boolean;
  canManage: boolean;
  onSaved: (settings: SchoolTaskSettings) => void;
}

/** School tasks settings of one school year; the backend decides whether the deadline has passed. */
export function SettingsSection({ settings, loadFailed, canManage, onSaved }: SettingsSectionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "nl";
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  if (!settings) return loadFailed ? <p role="alert" className="text-sm font-semibold text-rose-700 dark:text-rose-300">{t("schoolTasks.settingsPage.loadError", { defaultValue: "The settings could not be loaded." })}</p> : <Spinner />;

  const save = async (change: SchoolTaskSettingsChange) => {
    setSaving(true);
    setError("");
    try {
      onSaved(await teacherSchoolTasksApi.updateSettings(settings.schoolYearId, change));
      return true;
    } catch (cause: any) {
      setError(cause?.response?.data?.message || t("schoolTasks.settingsPage.saveError", { defaultValue: "The setting could not be saved." }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deadline = settings.selfSignupDeadline;
  const deadlineText = deadline ? formatLocalDate(deadline, locale) : t("schoolTasks.settingsPage.noDeadline", { defaultValue: "No deadline set" });
  return (
    <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{t("schoolTasks.settingsPage.deadline", { defaultValue: "Self-signup deadline" })}</h3>
        <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
          {deadlineText}
          {" · "}
          <Link to={`/school/years/${settings.schoolYearId}/edit`} className="font-bold text-indigo-600 hover:underline dark:text-indigo-300">{t("schoolTasks.settingsPage.editInSchoolYear", { defaultValue: "Change in the school year" })}</Link>
        </p>
      </div>
      <SwitchSetting
        label={t("schoolTasks.settingsPage.parentAccess", { defaultValue: "Open for parents" })}
        help={t("schoolTasks.settingsPage.parentAccessHelp", { defaultValue: "Parents see School tasks in the Parent Portal and can sign up until the deadline." })}
        checked={settings.parentAccessEnabled}
        canManage={canManage}
        saving={saving}
        confirmTitle={t("schoolTasks.settingsPage.parentAccessConfirmTitle", { defaultValue: "Confirm opening for parents" })}
        confirmText={t("schoolTasks.settingsPage.parentAccessConfirmText", { defaultValue: "Open School tasks of this school year for parents? They will see it in the Parent Portal." })}
        confirmLabel={t("schoolTasks.settingsPage.parentAccessConfirm", { defaultValue: "Open for parents" })}
        confirmWarning={settings.selfSignupClosed && deadline ? t("schoolTasks.settingsPage.deadlinePassedWarning", { defaultValue: "The self-signup deadline ({{deadline}}) has already passed. Adjust the deadline first if needed.", deadline: deadlineText }) : undefined}
        onChange={(parentAccessEnabled) => save({ parentAccessEnabled })}
      />
      <SwitchSetting
        label={t("schoolTasks.settingsPage.autoAssignment", { defaultValue: "Allow automatic assignment" })}
        help={t("schoolTasks.settingsPage.autoAssignmentHelp", { defaultValue: "After the deadline, proposals from the preview can be saved. Parents are then assigned." })}
        note={!settings.selfSignupClosed && deadline ? t("schoolTasks.settingsPage.availableFrom", { defaultValue: "Assigning only works from {{from}}.", from: formatLocalDate(deadline, locale, 1) }) : undefined}
        checked={settings.autoAssignmentApplyEnabled}
        canManage={canManage}
        saving={saving}
        confirmTitle={t("schoolTasks.settingsPage.confirmTitle", { defaultValue: "Confirm automatic assignment" })}
        confirmText={t("schoolTasks.settingsPage.confirmText", { defaultValue: "Allow automatic assignment for this school year? Saved proposals assign parents to tasks." })}
        confirmLabel={t("schoolTasks.settingsPage.confirm", { defaultValue: "Switch on" })}
        onChange={(autoAssignmentApplyEnabled) => save({ autoAssignmentApplyEnabled })}
      />
      {!canManage && <p className="text-sm text-slate-500 dark:text-slate-400">{t("schoolTasks.settingsPage.readOnly", { defaultValue: "You may view these settings but not change them." })}</p>}
      {error && <p role="alert" className="text-sm font-semibold text-rose-700 dark:text-rose-300">{error}</p>}
    </section>
  );
}

interface SwitchSettingProps {
  label: string;
  help: string;
  note?: string;
  checked: boolean;
  canManage: boolean;
  saving: boolean;
  confirmTitle: string;
  confirmText: string;
  confirmLabel: string;
  confirmWarning?: string;
  onChange: (enabled: boolean) => Promise<boolean>;
}

/** A setting that asks for confirmation before switching on; switching off happens at once. */
function SwitchSetting({ label, help, note, checked, canManage, saving, confirmTitle, confirmText, confirmLabel, confirmWarning, onChange }: SwitchSettingProps) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const button = "rounded-xl px-5 py-2.5 text-sm font-bold transition";
  return (
    <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
      <label className="flex items-center gap-3 text-sm font-bold text-slate-900 dark:text-slate-100">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={!canManage || saving || confirming}
          onChange={() => { if (checked) void onChange(false); else setConfirming(true); }}
          className="h-5 w-5 accent-indigo-600"
        />
        {label}
      </label>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{help}</p>
      {note && <p role="note" className="mt-2 text-sm font-semibold text-amber-700 dark:text-amber-300">{note}</p>}
      {confirming && <div role="group" aria-label={confirmTitle} className="mt-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-400/30 dark:bg-indigo-400/10">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{confirmText}</p>
        {confirmWarning && <p role="alert" className="mt-2 text-sm font-semibold text-amber-700 dark:text-amber-300">{confirmWarning}</p>}
        <div className="mt-3 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={() => setConfirming(false)} className={`${button} border border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200`}>{t("common.cancel", { defaultValue: "Cancel" })}</button>
          <button type="button" disabled={saving} onClick={async () => { if (await onChange(true)) setConfirming(false); }} className={`${button} bg-indigo-600 text-white hover:bg-indigo-700`}>{confirmLabel}</button>
        </div>
      </div>}
    </div>
  );
}
